/**
 * Truncated list cell for stats table.
 * Same visual style as TruncatedDropCell: inline baseline, copy-on-click.
 * Tags from rules (e.g. High Profit) shown regardless of dashboard date range —
 * tags always use the rule's date range (e.g. last-30-days).
 * For Media Devoted | Created By Ciro Autiero
 */

import type { FC } from 'react';

import { LeadCountBadge, LeadsListTags } from '@/features/smswiser-campaigns/components/shared';
import { useToast } from '@/providers/ToastProvider';

import {
  StatsHoverPopover,
  StatsTooltipSection,
  StatsTooltipTagsBlock,
} from '../../../StatsHoverPopover';
import { truncateEnd } from '../../../shared';

interface TruncatedListCellProps {
  label: string;
  listId?: string | null;
  /** Lead count for this list (from catalog). */
  leadCount?: number | null;
  /** Amount used today for remaining calculation. */
  usageToday?: number;
  /** Remaining = leadCount - usage (computed on backend when workDate provided). */
  remaining?: number | null;
  /** When false, hide "X left" for lists that have counts (only show N/A when list not fetched). */
  isDateRangeToday?: boolean;
  /** Performance tags from rules (rule date range, e.g. last-30-days). Shown even when dashboard is "today". */
  performanceTags?: string[];
  /** Tag + date range per rule for hover tooltip (e.g. High Profit — last 30 days). */
  performanceTagDetails?: Array<{ tag: string; dateRangePreset: string }>;
  /** Custom colors for tags (rule name -> hex). */
  tagColors?: Record<string, string>;
  /** True while performance tags are being fetched/refetched. */
  tagsLoading?: boolean;
}

export const TruncatedListCell: FC<TruncatedListCellProps> = ({
  label,
  listId,
  leadCount,
  usageToday,
  remaining,
  isDateRangeToday = true,
  performanceTags,
  performanceTagDetails,
  tagColors,
  tagsLoading = false,
}) => {
  const { success: toastSuccess, error: toastError } = useToast();
  const displayLabel = label || listId || '—';
  const truncatedLabel = truncateEnd(displayLabel, 32);

  const handleCopyLabel = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const text = truncatedLabel.full || displayLabel;
    if (!text || text === '—') return;
    navigator.clipboard
      .writeText(text)
      .then(() => toastSuccess('List name copied', 'Copied to clipboard'))
      .catch(() => toastError('Copy failed', 'Could not copy to clipboard'));
  };

  const handleCopyId = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!listId) return;
    navigator.clipboard
      .writeText(listId)
      .then(() => toastSuccess('List ID copied', 'Copied to clipboard'))
      .catch(() => toastError('Copy failed', 'Could not copy to clipboard'));
  };

  const hasCount =
    leadCount != null && Number.isFinite(leadCount);

  /* Show badge: today (always), or not-today only when list has no count (N/A). Hide when not-today + has count. */
  const showLeadCount = isDateRangeToday || !hasCount;

  /* Pass real count only when today AND fetched; otherwise N/A. */
  const showCount = isDateRangeToday && hasCount;

  const tagsLine = (performanceTags?.length ?? 0) > 0 || tagsLoading ? (
    <LeadsListTags
      performanceTags={performanceTags ?? []}
      tagColors={tagColors}
      className="inline-flex"
      compact
      disableTooltip
      loading={tagsLoading}
    />
  ) : null;

  const hoverContent = (
    <div className="flex flex-col gap-1.5 text-left">
      <StatsTooltipSection label="ID" valueClassName="font-mono">
        {listId || '—'}
      </StatsTooltipSection>
      <StatsTooltipSection label="Name" valueClassName="font-medium">
        {truncatedLabel.full || displayLabel || '—'}
      </StatsTooltipSection>
      {showLeadCount && (
        <StatsTooltipSection label="Leads">
          {showCount && leadCount != null
            ? `${remaining != null ? remaining : leadCount} left`
            : 'N/A'}
        </StatsTooltipSection>
      )}
      {((performanceTagDetails?.length ?? performanceTags?.length) ?? 0) > 0 && (
        <div>
          <StatsTooltipTagsBlock
            performanceTags={performanceTags}
            performanceTagDetails={performanceTagDetails}
            tagColors={tagColors}
          />
        </div>
      )}
    </div>
  );

  return (
    <StatsHoverPopover content={hoverContent}>
      <div className="flex min-w-0 flex-col gap-0.5 text-xs text-muted-foreground cursor-default">
        <div className="flex min-w-0 items-baseline gap-1.5">
          <button
            type="button"
            onClick={handleCopyId}
            title="Click to copy list ID"
            className="shrink-0 font-mono font-medium text-foreground cursor-pointer rounded px-0.5 -mx-0.5 hover:bg-muted/60 hover:text-primary disabled:pointer-events-none disabled:opacity-50"
            aria-label="Copy list ID"
            disabled={!listId}
          >
            {listId || '—'}
          </button>
          <button
            type="button"
            onClick={handleCopyLabel}
            title="Click to copy list name"
            className="min-w-0 truncate text-left text-inherit bg-transparent border-0 p-0 cursor-pointer rounded px-0.5 -mx-0.5 hover:bg-muted/60 hover:text-primary font-medium text-foreground/80"
            aria-label="Copy list name"
          >
            {truncatedLabel.display}
          </button>
          {tagsLine != null && (
            <span className="ml-1 inline-flex shrink-0 items-center">
              {tagsLine}
            </span>
          )}
          {showLeadCount && (
            <span className="ml-auto shrink-0">
              <LeadCountBadge
                count={showCount ? leadCount : undefined}
                usage={showCount ? usageToday : undefined}
                remaining={showCount ? remaining : undefined}
                compact
                emptyLabel="N/A"
                className="text-primary font-medium"
              />
            </span>
          )}
        </div>
      </div>
    </StatsHoverPopover>
  );
};
